import { useEffect, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { FileText, ChevronRight, Download } from "lucide-react";
import { api } from "../../lib/api";

/**
 * AdminReports — landing for the Reports section.
 * - Sidebar of available reports (driven by the backend report catalog so we
 *   can add reports server-side without redeploying the frontend).
 * - The actual report pages render via <Outlet />.
 */
export default function AdminReports() {
    const [catalog, setCatalog] = useState([]);
    const { pathname } = useLocation();

    useEffect(() => {
        api.get("/admin/reports/catalog").then(({ data }) => setCatalog(data || [])).catch(() => setCatalog([]));
    }, []);

    return (
        <div>
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold inline-flex items-center gap-2"><FileText size={20} /> Reports</h1>
                    <p className="text-sm text-muted-foreground mt-1">Downloadable analytics &amp; settlement reports.</p>
                </div>
            </div>

            <div className="grid lg:grid-cols-[260px_1fr] gap-6 mt-6">
                <aside className="space-y-2">
                    {catalog.map((r) => {
                        const to = `/admin/reports/${r.key}`;
                        const active = pathname === to;
                        return (
                            <Link key={r.key} to={to} data-testid={`report-${r.key}`}
                                className={`flex items-start gap-3 p-3 rounded-xl border transition ${active ? "border-primary bg-primary/5" : "border-border hover:bg-muted"}`}>
                                <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><FileText size={16} /></div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-bold">{r.title}</p>
                                    <p className="text-xs text-muted-foreground line-clamp-2">{r.description}</p>
                                </div>
                                <ChevronRight size={16} className="text-muted-foreground mt-1.5" />
                            </Link>
                        );
                    })}
                    {catalog.length === 0 && <p className="text-xs text-muted-foreground">Loading reports…</p>}
                </aside>

                <section>
                    {pathname === "/admin/reports" ? (
                        <div className="bg-card border border-border rounded-2xl p-10 text-center">
                            <Download size={32} className="text-muted-foreground mx-auto" />
                            <p className="mt-3 font-semibold">Pick a report from the left</p>
                            <p className="text-sm text-muted-foreground">Each report supports a date range, optional filters, and a CSV download.</p>
                        </div>
                    ) : (
                        <Outlet />
                    )}
                </section>
            </div>
        </div>
    );
}
